import { createElement, type ReactElement } from "react";
import {
  AccountState,
  EmailAuthMethod,
  WarmupStatus,
  isAuthFailedSuspendReason,
  isUserResumable,
  isWarmupManuallyPausable,
} from "@trulyinbox/types";
import type {
  DomainSecurityFieldKey,
  EmailAccountListItem,
  StatusBucket,
} from "@/lib/email-accounts";
import { isPersonalEmail } from "@/lib/email-domain";
import { ProviderIcon } from "@/icons/ProviderIcon";
import { MONTHS_SHORT } from "@/lib/date";
import { capitalize } from "@/lib/string";

/* ──────────────────────────────────────────────────────────────────────────
 * Canonical warmup-STATE buckets (Phase 7).
 *
 * `StatusBucket` describes warmup STATE (active / throttled / paused /
 * suspended / maintenance / disconnected / setup), NOT
 * deliverability HEALTH. It drives the status badge, the status filter, and
 * the count pills. The API's `account.statusBucket` (derived server-side from
 * `accountState` via `ACCOUNT_STATE_BUCKET`) is the source of truth —
 * `accountStatusBucket()` prefers it and only falls back to deriving from
 * `accountState` for robustness.
 *
 * `disconnected` was split out of the internal `setup` bucket so a
 * disconnected account is independently filterable and reads its own badge
 * label ("Disconnected") instead of the generic "Setting up" every other
 * connection state (CONNECTED, DNS_CHECKING, DNS_FAILED, DNS_PASSED,
 * WARMUP_READY) still shares.
 *
 * Deliverability HEALTH (`deriveHealthStatus`, `deliverabilityScore`,
 * `inboxPlacement`, the donut, the placement bar) is a SEPARATE visual concern
 * and is intentionally kept intact below — the two are never folded together.
 * ────────────────────────────────────────────────────────────────────────── */

/** Bucket → semantic UI tone for the status badge/chip. `suspended` is `bad`
 * (system-initiated, needs attention); `cooldown` is `info` (system-initiated
 * too, but it resumes on its own — nothing for the user to do);
 * `paused`/`disconnected` are `neutral` (user-initiated or a plain connection
 * break, not itself a deliverability verdict). */
export const STATUS_BUCKET_TONE: Record<
  StatusBucket,
  "good" | "warn" | "bad" | "info" | "neutral"
> = {
  active: "good",
  throttled: "warn",
  paused: "neutral",
  suspended: "bad",
  cooldown: "info",
  maintenance: "good",
  disconnected: "neutral",
  setup: "neutral",
};

/** Bucket → display label for the status badge/chip + filter rows. */
export const STATUS_BUCKET_LABEL: Record<StatusBucket, string> = {
  active: "Active",
  throttled: "Throttled",
  paused: "Paused",
  suspended: "Suspended",
  cooldown: "Cooling down",
  maintenance: "Maintenance",
  disconnected: "Disconnected",
  setup: "Setting up",
};

/** Bucket → token colour for the filter-row dot (mirrors the chip tone). */
export const STATUS_BUCKET_COLOR: Record<StatusBucket, string> = {
  active: "var(--good-500)",
  throttled: "var(--warn-500)",
  paused: "var(--ink-400)",
  suspended: "var(--bad-500)",
  cooldown: "var(--info-500)",
  maintenance: "var(--good-500)",
  disconnected: "var(--purple-500)",
  setup: "var(--ink-400)",
};

// Map any canonical 16-state `accountState` to its bucket. Mirrors the
// backend `ACCOUNT_STATE_BUCKET` (`@trulyinbox/types`); kept local because that
// package isn't a web dependency (only `@trulyinbox/errors` is). Used as the
// fallback when the API omits `statusBucket` on a degraded response.
const ACCOUNT_STATE_TO_BUCKET: Record<string, StatusBucket> = {
  WARMUP_ACTIVE: "active",
  WARMUP_THROTTLED: "throttled",
  WARMUP_PAUSED: "paused",
  SUSPENDED_AUTH: "suspended",
  SUSPENDED_BOUNCE: "suspended",
  SUSPENDED_SPAM: "suspended",
  SUSPENDED_BLACKLIST: "suspended",
  SUSPENDED_PROVIDER: "suspended",
  SUSPENDED_PLATFORM: "suspended",
  MAINTENANCE: "maintenance",
  DISCONNECTED: "disconnected",
  CONNECTED: "setup",
  DNS_CHECKING: "setup",
  DNS_FAILED: "setup",
  DNS_PASSED: "setup",
  // Mailbox send/receive verification — the second hard gate, after DNS. Both
  // sit in `setup` to mirror the server's `ACCOUNT_STATE_BUCKET`, so the
  // client-side fallback can never disagree with the value the API sends.
  // The FAILED one takes a badge-label override below: the bucket is right
  // (the mailbox has never warmed) but "Setting up" is the wrong story for a
  // mailbox that is stuck and needs a human.
  MAILBOX_VERIFYING: "setup",
  MAILBOX_VERIFY_FAILED: "setup",
  WARMUP_READY: "setup",
  SUSPENDED_VERIFICATION: "suspended",
};

const STATUS_BUCKETS = new Set<string>([
  "active",
  "throttled",
  "paused",
  "suspended",
  "cooldown",
  "maintenance",
  "disconnected",
  "setup",
]);

/**
 * The canonical state bucket for an account. Prefers the API-provided
 * `statusBucket`; falls back to mapping `accountState` (then `setup`) so a
 * degraded response never throws. This is the single source for the status
 * badge / filter / counts.
 */
/**
 * The two fields the status bucket is derived from. Declared as a `Pick`, not
 * the whole `EmailAccountListItem`, so callers that legitimately hold only a
 * slim projection of an account can use these predicates instead of re-deriving
 * the rule — a second copy of a warmup rule is exactly what TRU-1044 was. A full
 * `EmailAccountListItem` still satisfies it structurally, so every existing call
 * site is unaffected.
 */
export type StatusBucketFields = Pick<
  EmailAccountListItem,
  "statusBucket" | "accountState"
>;

/**
 * Everything the warmup-control predicates below read — and nothing else. This
 * is the CONTRACT for any payload that wants to drive a Pause/Resume/Start
 * control: add a field read to one of those predicates and this type must grow
 * with it, which fails the build at every producer that doesn't carry it.
 *
 * `dwd-workspace`'s Manage-Mailboxes rows are the second producer (the accounts
 * table's full list item is the first): `GET /dwd-workspace/:id/connected-emails`
 * sends only the 4 warmup fields and the row composes the rest from data it
 * already has — see `resolveWarmupAction` in `features/dwd-workspace/dwd-helpers.ts`.
 */
export type WarmupControlAccount = StatusBucketFields &
  Pick<
    EmailAccountListItem,
    | "status"
    | "emailAddress"
    | "warmupStatus"
    | "warmupPausedReason"
    | "structuredStatus"
    | "dwdWorkspaceId"
    | "partnerIntegrationId"
    | "domainSecurity"
  >;

/**
 * Bucket from the canonical `accountState` ALONE, for payloads that carry no
 * server-derived `statusBucket`.
 *
 * The mailbox-switcher lookup (`GET /email-accounts/lookup`) is the producer
 * that needs this: deriving `statusBucket` server-side is a `CASE` expression
 * over a joined warmup-state row, and that endpoint exists precisely to avoid
 * the join. Exported as a function rather than the raw map so the `?? "setup"`
 * fallback for an unrecognised state stays in one place — `accountStatusBucket`
 * below is this function plus the "prefer the server's value" rule.
 */
export function accountStateBucket(accountState: string): StatusBucket {
  return ACCOUNT_STATE_TO_BUCKET[accountState] ?? "setup";
}

export function accountStatusBucket(a: StatusBucketFields): StatusBucket {
  if (STATUS_BUCKETS.has(a.statusBucket)) return a.statusBucket as StatusBucket;
  return accountStateBucket(a.accountState);
}

/**
 * The status badge/chip label for an account. Almost always just
 * `STATUS_BUCKET_LABEL[accountStatusBucket(a)]` — but `accountState` is
 * checked directly for `DISCONNECTED` first, ahead of the server
 * `statusBucket`. `disconnected` is a brand-new public bucket (split out of
 * `setup`): a real `accountState = 'DISCONNECTED'` row written by the OLD
 * server code (before the DISCONNECTED-badge fix shipped) still carries
 * `statusBucket: 'setup'` until the one-time DB backfill
 * (`packages/database/schema-changes/2026-08-03-disconnected-accountstate-backfill.sql`)
 * runs against that environment — `accountStatusBucket()`'s "prefer the
 * server value" rule would otherwise keep reading the pre-fix bucket for
 * every already-disconnected account until that SQL is applied by hand.
 * Without this override the chip would read stale "Setting up" instead of
 * "Disconnected" for that entire window.
 */
export function accountStatusLabel(a: EmailAccountListItem): string {
  if (a.accountState === "DISCONNECTED")
    return STATUS_BUCKET_LABEL.disconnected;
  // Both mailbox-verification states bucket to `setup`, whose generic
  // "Setting up" label is actively misleading for them: one is a check the
  // user can wait out, the other is a mailbox that is stuck until someone
  // fixes it. Same override precedent as DISCONNECTED above — a state-level
  // label without splitting a new public filter bucket for two states.
  if (a.accountState === "MAILBOX_VERIFYING") return "Checking mailbox";
  if (a.accountState === "MAILBOX_VERIFY_FAILED") return "Mailbox check failed";
  return STATUS_BUCKET_LABEL[accountStatusBucket(a)];
}

/**
 * Is this account waiting on, or blocked by, the mailbox send/receive check?
 *
 * Deliberately covers all three states — the two pre-warmup ones and the
 * suspended one — because they share a single user-facing explanation ("we
 * could not confirm this mailbox can send and receive"), even though they sit
 * in different buckets and have different remedies.
 */
export function isMailboxVerificationState(a: EmailAccountListItem): boolean {
  return (
    a.accountState === "MAILBOX_VERIFYING" ||
    a.accountState === "MAILBOX_VERIFY_FAILED" ||
    a.accountState === "SUSPENDED_VERIFICATION"
  );
}

/** Whether the mailbox check is still running (as opposed to having failed). */
export function isMailboxVerificationPending(a: EmailAccountListItem): boolean {
  return a.accountState === "MAILBOX_VERIFYING";
}

/**
 * Is this account barred from RECEIVING warmup mail?
 *
 * Deliberately NOT part of {@link isMailboxVerificationState} and NOT reflected
 * in the status badge, because it is not a status: the account is warming
 * normally and sending at full volume. Only its incoming has stopped.
 *
 * It still needs surfacing somewhere, which is what this is for. A barred
 * account's received count quietly drops to zero and its engagement metrics
 * follow — and because nothing else in the UI explains why, the user's only
 * reading is that their warmup broke.
 */
export function isReceiveCheckFailed(a: EmailAccountListItem): boolean {
  return a.receiveCheckFailed === true;
}

/**
 * The explanation for an account barred from receiving. Exported so the Action
 * items page shows the SAME words as the account detail — one explanation, not
 * two drifting phrasings of it.
 */
export const RECEIVE_CHECK_WARNING =
  "This mailbox isn't receiving our test mail, so other accounts have stopped " +
  "sending to it. It keeps sending its own warmup email. We re-test it " +
  "automatically and restore it once mail arrives again.";

/**
 * The warning to show on an account barred from receiving, or `null`.
 *
 * Returns copy rather than a boolean so the one explanation lives beside the
 * predicate that decides when it applies — the pair drifting is how a UI ends up
 * showing a warning that describes a different condition.
 */
export function receiveCheckWarning(a: EmailAccountListItem): string | null {
  if (!isReceiveCheckFailed(a)) return null;
  return RECEIVE_CHECK_WARNING;
}

/**
 * Is this bucket a system-initiated stop — `suspended`, or `cooldown` (the
 * rate-driven `SUSPENDED_SPAM` / `SUSPENDED_BOUNCE` subset that resumes on its
 * own)? Both are `SUSPENDED_*` states underneath, so every warmup control that
 * asks "is this suspended" must treat them alike: a cooldown account is not
 * enableable (`022406`), and Resume is shown locked rather than Start.
 */
export function isSuspendedBucket(bucket: StatusBucket): boolean {
  return bucket === "suspended" || bucket === "cooldown";
}

/** Whether the account is in any system-initiated `SUSPENDED_*` state. */
export function isSuspended(a: StatusBucketFields): boolean {
  return isSuspendedBucket(accountStatusBucket(a));
}

export type HealthStatus = "healthy" | "warming" | "risk" | "crit" | "paused";

/** Health status → semantic UI tone, used by StatusBadge/Chip across the
 * table, the expanded row, and the detail header. */
export const STATUS_TONE: Record<
  HealthStatus,
  "good" | "warn" | "bad" | "neutral"
> = {
  healthy: "good",
  warming: "warn",
  risk: "bad",
  crit: "bad",
  paused: "neutral",
};

export const HEALTH_STATUS_LABEL: Record<HealthStatus, string> = {
  healthy: "Healthy",
  warming: "Warming up",
  risk: "Needs attention",
  crit: "Needs attention",
  paused: "Paused",
};

export const HEALTH_STATUS_COLOR: Record<HealthStatus, string> = {
  healthy: "var(--good-500)",
  warming: "var(--warn-500)",
  risk: "var(--bad-500)",
  crit: "var(--bad-600)",
  paused: "var(--ink-400)",
};

/** Donut + placement-bar fill colour per health status. Intentionally distinct
 * from `HEALTH_STATUS_COLOR` for `crit`: the donut uses the lighter `--bad-500`
 * shade (not `--bad-600`) so the dense table chart reads less alarmingly than
 * the chip/label. Both `risk` and `crit` therefore share `--bad-500` here. */
/** Inbox-placement bar fill colour by percentage (0..100): ≥80 good, ≥50 warn,
 * else bad. Shared by the table's placement cell. */
export function placementColor(v: number): string {
  if (v >= 80) return "var(--good-500)";
  if (v >= 50) return "var(--warn-500)";
  return "var(--bad-500)";
}

/**
 * Deliverability bands for the accounts table's Deliverability column
 * (`DeliverabilityCell`): **80+ green, 70–80 orange, below 70 red**, with the
 * boundaries owned by the two constants below rather than inlined — the same
 * shape `placementColor` uses one screen over.
 *
 * A band is `>= min`, so exactly 80 reads green and exactly 70 orange. The
 * ranges as briefed overlap at both boundaries ("0-70 red, 70-80 orange, 80+
 * green"); this is the reading that leaves no value unassigned.
 *
 * `unmeasured` is NOT a fourth range, it is the absence of one, and it exists
 * because painting it red would be a confidently wrong statement. A stored
 * `0` means "no measurement yet", not 0% delivery: the rate is a point-in-time
 * snapshot over a rolling 7-day send window, so an account between cycles or
 * freshly connected recomputes to 0 while delivering perfectly well. That is
 * the same rule `deriveHealthStatus` already applies (see its `rate > 0`
 * guards), and it is safe because a *genuine* 0% account — real sample, all
 * bouncing — is already PAUSED or THROTTLED by the threshold evaluator.
 *
 * A paused or disconnected account is `unmeasured` too, for a different
 * reason: its last rate is stale, and colouring a stale figure green claims
 * a health it isn't currently demonstrating. The Status column is what says
 * why it's grey.
 */
export const DELIVERABILITY_GOOD_MIN = 80;
export const DELIVERABILITY_WARN_MIN = 70;

export type DeliverabilityBand = "good" | "warn" | "bad" | "unmeasured";

export function deliverabilityBand(
  account: EmailAccountListItem,
): DeliverabilityBand {
  if (account.status === "DISCONNECTED" || account.warmupStatus === "PAUSED") {
    return "unmeasured";
  }
  // `decimal(5,2)` arrives as a string at runtime — coerce before comparing,
  // or `'70' >= 80` silently compares as text (mysql2 serialises decimal columns as strings).
  const rate =
    account.deliverabilityRate == null
      ? null
      : Number(account.deliverabilityRate);
  if (rate === null || !Number.isFinite(rate) || rate <= 0) return "unmeasured";
  if (rate >= DELIVERABILITY_GOOD_MIN) return "good";
  if (rate >= DELIVERABILITY_WARN_MIN) return "warn";
  return "bad";
}

export const DELIVERABILITY_BAND_COLOR: Record<DeliverabilityBand, string> = {
  good: "var(--good-500)",
  warn: "var(--warn-500)",
  bad: "var(--bad-500)",
  unmeasured: "var(--ink-400)",
};

/** The word beside the Deliverability ring for a measured band. */
export const DELIVERABILITY_BAND_LABEL: Record<Exclude<DeliverabilityBand, "unmeasured">, string> = {
  good: "Good",
  warn: "Fair",
  bad: "Poor",
};

/**
 * The word beside the ring, for every row — so each one reads ring, number,
 * word. An `unmeasured` row gets the REASON there is no verdict instead of a
 * verdict (its number is stale or not a measurement; see `deliverabilityBand`):
 * same branches, same order.
 */
export function deliverabilityLabel(account: EmailAccountListItem): string {
  const band = deliverabilityBand(account);
  if (band !== "unmeasured") return DELIVERABILITY_BAND_LABEL[band];
  if (account.status === "DISCONNECTED") return "Disconnected";
  if (account.warmupStatus === "PAUSED") return "Paused";
  return "No data";
}

/** The Deliverability column's fill: donut arc AND the percentage beside it,
 * so the two can never disagree about the same number. */
export function deliverabilityColor(account: EmailAccountListItem): string {
  return DELIVERABILITY_BAND_COLOR[deliverabilityBand(account)];
}

/**
 * The three colours a SELECTED status tile paints with, per bucket
 * (`EmailAccountsStatusSummaryBar`). Selecting Disconnected tints the tile
 * purple, Suspended red, and so on — the filter you picked is stated in that
 * status's own colour rather than in one shared accent, so the strip says
 * *which* filter is on, not merely *that* one is.
 *
 * Three roles, not one, because a single hue can't do all three jobs: the 500
 * is right for a 1px border and wrong for text on its own pale fill (orange at
 * `--brand-500` is ~2.2:1 on white — see `tokens.css`). Text takes the `-600`,
 * which the ramps define as darker in light and LIGHTER in dark, so both themes
 * read without a second map.
 *
 * `disconnected` and `paused` are the two that mix rather than reach for a
 * token: there is no `--purple-50`/`--purple-600` ramp, and `paused` is drawn
 * from the ink scale by design (it is not a health verdict). Mixing against
 * `--paper` / `--ink-900` keeps both theme-correct for the same reason the
 * ramps are — the thing being mixed INTO flips with the theme.
 */
export interface StatusAccent {
  /** Tile background. */
  fill: string;
  /** 1px tile border. */
  border: string;
  /** Figure + label. */
  ink: string;
}

export const STATUS_BUCKET_ACCENT: Record<StatusBucket, StatusAccent> = {
  active: { fill: "var(--good-50)", border: "var(--good-500)", ink: "var(--good-600)" },
  maintenance: { fill: "var(--good-50)", border: "var(--good-500)", ink: "var(--good-600)" },
  throttled: { fill: "var(--warn-50)", border: "var(--warn-500)", ink: "var(--warn-600)" },
  suspended: { fill: "var(--bad-50)", border: "var(--bad-500)", ink: "var(--bad-600)" },
  cooldown: { fill: "var(--info-50)", border: "var(--info-500)", ink: "var(--info-500)" },
  paused: { fill: "var(--ink-100)", border: "var(--ink-400)", ink: "var(--ink-700)" },
  disconnected: {
    fill: "color-mix(in srgb, var(--purple-500) 12%, var(--paper))",
    border: "var(--purple-500)",
    ink: "color-mix(in srgb, var(--purple-500) 78%, var(--ink-900))",
  },
  setup: { fill: "var(--brand-50)", border: "var(--brand-500)", ink: "var(--brand-700)" },
};

/**
 * The accent for the strip's "Total" tile, which is the NO-FILTER state rather
 * than a bucket — so it stays on the app's own brand accent while every real
 * bucket answers in its own colour.
 */
export const TOTAL_ACCENT: StatusAccent = {
  fill: "var(--brand-50)",
  border: "var(--brand-500)",
  ink: "var(--brand-700)",
};

const RAMPUP_TOTAL_DAYS = 14;

export function deriveHealthStatus(
  account: EmailAccountListItem,
): HealthStatus {
  if (account.status === "DISCONNECTED" || account.warmupStatus === "PAUSED") {
    return "paused";
  }

  // Blocking DNS records (SPF + MX always; DKIM only for business/custom-domain
  // accounts; DMARC never) paint the account "risk". See `dnsBlockingRecords`.
  if (dnsBlockingRecords(account).length > 0) return "risk";

  if (account.warmupStatus === "THROTTLED") return "crit";

  // `deliverabilityRate` is a 0..100 percentage (see EmailAccountListItem type). Like
  // `inboxPlacementScore` it is a decimal(5,2) column that arrives as a string
  // at runtime, so coerce before comparing (mysql2 serialises decimal columns as strings).
  //
  // A stored `0` is treated as "no measurement yet", NOT a genuine 0%
  // deliverability. The rate is a point-in-time snapshot computed from a rolling
  // 7-day send window during pool evaluation; an account that hasn't sent in 7+
  // days (between cycles, freshly connected) recomputes to 0 even though its
  // real delivery is healthy. A *genuine* 0% account (real sample, all bouncing)
  // is already PAUSED/THROTTLED by the threshold evaluator and returns
  // crit/paused above — so excluding 0 here never hides a real failure.
  const rate =
    account.deliverabilityRate == null
      ? null
      : Number(account.deliverabilityRate);
  if (rate !== null && rate > 0 && rate < 35) return "crit";
  if (rate !== null && rate > 0 && rate < 70) return "risk";

  const day = account.warmupRampupDay ?? 0;
  if (account.warmupStatus === "RUNNING" && day < RAMPUP_TOTAL_DAYS)
    return "warming";

  return "healthy";
}

export const PROVIDER_LABELS: Record<string, string> = {
  GMAIL: "Gmail",
  GOOGLE_WORKSPACE: "Google Workspace",
  OUTLOOK: "Outlook",
  MS365: "MS365",
  AZURE: "Azure",
  OTHERS: "Others",
  ZOHO: "Zoho",
  YAHOO: "Yahoo",
  SENDGRID: "SendGrid",
  MAILGUN: "Mailgun",
  AMAZON_SES: "Amazon SES",
  ELASTIC_EMAIL: "Elastic Email",
  YANDEX: "Yandex",
  GODADDY: "Go Daddy",
};

const PROVIDER_COLORS: Record<string, string> = {
  GMAIL: "#ea4335",
  GOOGLE_WORKSPACE: "#ea4335",
  OUTLOOK: "#0a64bd",
  MS365: "#0a64bd",
  AZURE: "#0a64bd",
  OTHERS: "var(--ink-500)",
};

export function providerLabel(provider: string): string {
  return PROVIDER_LABELS[provider.toUpperCase()] ?? provider;
}

/**
 * Display label for the "Provider" UI. Shows "Others" for a password-connected
 * branded ESP (e.g. Gmail added via SMTP) instead of the brand name — display
 * only, `provider` itself stays untouched since it still drives warmup pacing.
 */
// The closed set of *technical* `EmailProvider` values that are actually
// OAuth-capable. Deliberately NOT `PROVIDER_LABELS`'s key set — that map now
// also carries the cataloged ESP brand labels (ZOHO, SENDGRID, ...), which
// are SMTP-transport only and must never collapse to "Others" here.
// GMAIL excluded deliberately: a password-connected gmail.com mailbox should
// still show the "Gmail" brand label, not collapse to "Others".
const OAUTH_CAPABLE_PROVIDERS = new Set([
  "GOOGLE_WORKSPACE",
  "OUTLOOK",
  "MS365",
  "AZURE",
]);

export function providerDisplayLabel(a: {
  provider: string;
  authMethod: string;
}): string {
  const key = a.provider.toUpperCase();
  const isOAuthCapableBrand = OAUTH_CAPABLE_PROVIDERS.has(key);
  if (isOAuthCapableBrand && a.authMethod === EmailAuthMethod.PASSWORD) {
    return PROVIDER_LABELS.OTHERS;
  }
  return providerLabel(a.provider);
}

export function providerColor(provider: string): string {
  return PROVIDER_COLORS[provider.toUpperCase()] ?? "var(--ink-500)";
}

// Brand-color provider icon as a ReactElement. Lives next to providerLabel /
// providerColor so call sites have a single import surface for the
// provider-by-string trio. Uses `React.createElement` to keep this file as
// `.ts` — no JSX, no rename ripple.
export function providerIcon(provider: string, size = 14): ReactElement {
  return createElement(ProviderIcon, { provider, size });
}

export function ownerFromAccount(a: EmailAccountListItem): string {
  if (a.senderName && a.senderName.trim().length > 0) return a.senderName;
  const local = a.emailAddress.split("@")[0] ?? a.emailAddress;
  return local;
}

export function formatConnectedDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.getDate()} ${MONTHS_SHORT[d.getMonth()]}`;
}

export function deliverabilityScore(a: EmailAccountListItem): number {
  // `deliverabilityRate` is already a 0..100 percentage (mysql2 serialises the
  // decimal as a string, hence `Number`). Rounded UP, like every other
  // deliverability % in the product.
  return Math.ceil(Number(a.deliverabilityRate ?? 0));
}

export function rampupTotalDays(): number {
  return RAMPUP_TOTAL_DAYS;
}

// Warmup state predicates — shared by the detail-page action bar and the
// table's per-row "More actions" menu so both surface the same Pause vs
// Start/Resume contract. THROTTLED and MAINTENANCE both still send mail and
// must surface Pause, not Start.
//
// DERIVED from `isWarmupManuallyPausable` (@trulyinbox/types) rather than re-listing the
// three literals: the API's pause guard is keyed to the same set via
// `MANUALLY_PAUSABLE_STATES`, and this file having its own copy is exactly how
// the two drifted in TRU-1044 (web offered Pause on THROTTLED/MAINTENANCE, the
// API 400'd `022501`, and those accounts had no working warmup control at all).
//
// Reads BOTH columns through the API's own pause predicate: a reconnect can
// leave the `warmupStatus` mirror at PENDING while `accountState` is still
// THROTTLED, and reading the mirror alone offered "Start warmup" that the API
// rejected with `AlreadyRunning` (022403).
export function isAccountSending(a: WarmupControlAccount): boolean {
  return (
    a.status === "CONNECTED" &&
    isWarmupManuallyPausable(
      a.accountState as AccountState,
      a.warmupStatus as WarmupStatus,
    )
  );
}

export function canEnableWarmup(a: WarmupControlAccount): boolean {
  // A suspended account cannot be enabled — the backend now 400s with
  // `EnableWarmup.Suspended` (`022406`); it must show Resume, not Start/Enable.
  if (isSuspended(a)) return false;
  return (
    a.status === "CONNECTED" &&
    (a.warmupStatus === "PENDING" || a.warmupStatus === "PAUSED")
  );
}

/**
 * Whether the Resume action applies. A connected, never-enabled account is
 * "Start", not "Resume" (that stays the enable affordance, gated by
 * `canEnableWarmup`).
 *
 * NARROWER than the state bucket alone, which is all this used to test. Not
 * every suspension is the user's to lift: the rate-driven bounce/spam family is
 * measured over a rolling 7-day window that a resume cannot clear, so releasing
 * it just re-suspends on the next threshold tick — while each manual resume
 * restamps `warmupStartedAt` and throws away the account's ramp progress. Those
 * recover through the automatic sweep instead.
 *
 * Shares the ONE predicate with both backend gates (`@trulyinbox/types`
 * `isUserResumable` — also read by the api's `resumeWarmup` and the worker's
 * `resumeFromPause`) rather than re-deriving the rule here. Web and API each
 * owning a copy of a warmup predicate is exactly how TRU-1044 shipped: the web
 * offered Pause on states the API rejected with `022501`, leaving those accounts
 * with no working control at all.
 */
export function canResumeWarmup(a: WarmupControlAccount): boolean {
  const bucket = accountStatusBucket(a);
  // A user pause is always the user's to undo. Keyed on the bucket, NOT run
  // through `isUserResumable`, so a row whose `accountState` is unmapped or
  // stale can never lose its Resume button — the bucket is the server's own
  // projection and is authoritative for "the user stopped this".
  if (bucket === "paused") return true;
  if (!isSuspendedBucket(bucket)) return false;
  return isUserResumable(a.accountState as AccountState, a.warmupPausedReason);
}

/**
 * Suspended, but not by anything the user may lift — the state where Resume is
 * shown disabled rather than hidden. Distinct from "not resumable" generally: a
 * CONNECTED never-enabled account is also not resumable, and wants Start.
 */
export function isResumeLocked(a: WarmupControlAccount): boolean {
  return isSuspendedBucket(accountStatusBucket(a)) && !canResumeWarmup(a);
}

/**
 * A partner-managed AUTH_FAILED lock is the one resume-locked case where BOTH
 * the server's `next_step` ("Reconnect the mailbox…") and the local fallback
 * ("resumes automatically once the signal recovers") are wrong: nothing in
 * this UI can reconnect a partner-managed mailbox, and it does NOT resume on
 * its own — only the org owner, via the partner's connect API, can clear it.
 * `null` when the account is not in that specific state.
 *
 * Exported separately from `warmupResumeBlockedReason` (which still returns
 * this same message as part of its own result — every OTHER consumer keeps
 * reading it there) because `WarmupToggle` needs to tell this one case apart:
 * the message is too long for a tooltip, so that control surfaces it as a
 * toast on the attempted click instead of disabling itself with a wall of
 * text to hover. See `action-item-copy.ts`'s matching branch, which this
 * mirrors.
 */
export function partnerReconnectNotice(a: WarmupControlAccount): string | null {
  if (!isResumeLocked(a)) return null;
  if (
    a.partnerIntegrationId != null &&
    a.accountState === AccountState.SUSPENDED_AUTH &&
    isAuthFailedSuspendReason(a.warmupPausedReason)
  ) {
    return "This mailbox is managed by a partner integration. Ask your organization owner to reconnect it — they can do this by calling the connect API again with a fresh access token.";
  }
  return null;
}

/**
 * Why Resume is unavailable on a locked suspension, for the disabled control's
 * tooltip. `null` when the account is not in that state.
 *
 * Prefers the server's own `structuredStatus.next_step`, which is built at
 * transition time by `buildStructuredStatus` and already carries the accurate,
 * per-reason wording (including the recovery horizon). The local fallback only
 * covers a row with no transition record yet.
 *
 * This is what closed the "Warmup is complete for this mailbox" bug: the toggle
 * had no suspended branch at all, so every suspended mailbox fell through
 * `canEnableWarmup` (false for suspended, correctly — enable 400s with
 * `022406`) onto the graduation copy, telling users their warmup had FINISHED
 * when it had been stopped for bounces.
 */
export function warmupResumeBlockedReason(
  a: WarmupControlAccount,
): string | null {
  if (!isResumeLocked(a)) return null;
  // Checked before `next_step` so a stale/never-transitioned row can't paper
  // back over it with the generic copy.
  const partnerNotice = partnerReconnectNotice(a);
  if (partnerNotice) return partnerNotice;
  const nextStep = a.structuredStatus?.next_step;
  if (nextStep) return nextStep;
  const cause = a.warmupPausedReason
    ? humanizeWarmupReason(a.warmupPausedReason)
    : "a deliverability issue";
  return `Warmup was stopped because of ${cause}. It resumes automatically once the signal recovers. Resuming now would only stop it again.`;
}

/**
 * Whether the account needs the structured-status attention panel: resumable
 * (paused/suspended) or a non-good status-bucket tone. Shared by
 * `AccountStatusPanel` (renders the panel) and the detail-page hero card
 * (which shows a "next step" strip only when this is false, so the two never
 * both surface the same `structuredStatus.next_step` copy at once).
 */
export function accountNeedsAttention(a: EmailAccountListItem): boolean {
  return (
    canResumeWarmup(a) || STATUS_BUCKET_TONE[accountStatusBucket(a)] !== "good"
  );
}

// `pauseWarmup` returns optimistically with `warmupStatus = PAUSED` before the
// warmup-worker commits its transaction; `warmupPausedAt` is the DB-backed
// confirmation. When the API says PAUSED but the worker hasn't written
// `warmupPausedAt` yet, the row is "pause in flight" — the UI shows a muted
// chip / spinner so the user knows the async operation is still resolving.
export function isPausePending(a: EmailAccountListItem): boolean {
  return a.warmupStatus === "PAUSED" && a.warmupPausedAt == null;
}

// Warmup reasons arrive as engine codes from the threshold evaluator /
// state machine — `HARD_BOUNCE`, `BOUNCE_RATE_6.25_PCT_NEW_DOMAIN`,
// `SPAM_RATE_3.50_PCT_ESTABLISHED`, `INBOX_PLACEMENT_55.00_PCT_NEW_MAILBOX`.
// We surface a short human phrase, not the raw metric, so the chip reads
// "Paused · too many bounces". Prefix-matched and data-driven so the
// SUSPENDED_* reasons from the Platform Health & Reliability project
// (TRU-254 follow-up) slot in by adding rows here.
const WARMUP_REASON_PHRASES: ReadonlyArray<readonly [string, string]> = [
  ["HARD_BOUNCE", "a hard bounce"],
  ["BOUNCE_RATE", "too many bounces"],
  ["SPAM_RATE", "high spam rate"],
  ["INBOX_PLACEMENT", "low inbox placement"],
  // SendFailureTracker (email-worker) — 3 counted send-time failures inside one
  // fixed 24h window. Only reached on the legacy path where `structuredStatus`
  // is null; when it is present, `accountStatusReason` prefers its `why`, which
  // the state machine fills with the failure count.
  ["SEND_FAILURES", "repeated send failures"],
  // `handleAuthFailed` — a dead OAuth grant, SMTP password, or partner-side
  // auth. Reachable through `warmupResumeBlockedReason` since `isUserResumable`
  // started locking Resume for this reason (TRU-1257 follow-up); like the
  // others, the real guidance ("reconnect the mailbox") comes from
  // `structuredStatus.next_step` once a transition row exists — this is only
  // the local fallback's cause phrase.
  ["AUTH_FAILED", "a sign-in failure"],
];

export function humanizeWarmupReason(raw: string): string {
  const code = raw.trim().toUpperCase();
  for (const [prefix, phrase] of WARMUP_REASON_PHRASES) {
    if (code.startsWith(prefix)) return phrase;
  }
  // Unknown code: sentence-case the leading words and drop any trailing
  // numeric metric tail (`_12.00_PCT_NEW_DOMAIN`), so we never show raw
  // SCREAMING_SNAKE noise (e.g. "MANUAL_OVERRIDE" → "Manual override").
  const words = code
    .replace(/_\d.*$/, "")
    .replace(/_/g, " ")
    .toLowerCase();
  return capitalize(words);
}

// The reason relevant to the account's *current* state: pause reason when
// paused, throttle reason when throttled, otherwise none. Returns the raw
// code — callers pass it through `humanizeWarmupReason` for display.
export function warmupStatusReason(a: EmailAccountListItem): string | null {
  if (a.warmupStatus === "PAUSED") return a.warmupPausedReason ?? null;
  if (a.warmupStatus === "THROTTLED") return a.throttleReason ?? null;
  return null;
}

/**
 * The org-level "Subscription paused" overlay for a mailbox's displayed status.
 *
 * While the org's subscription is PAUSED, EVERY mailbox reads "Paused ·
 * Subscription paused", whatever its real state — the cascade genuinely pauses
 * the sending ones and relabels the already-paused ones, and the gates hold the
 * rest, so no mailbox is warming and showing a per-row state would suggest
 * otherwise.
 *
 * Pure, and returns `null` when not paused, so a call site reads:
 *
 *   const overlay = orgPausedOverlay(isPaused);
 *   const label = overlay?.label ?? accountStatusLabel(a);
 *
 * **Display only.** Server `?status=` filters and the status counts keep using
 * the REAL states, so a "Suspended" filter still finds suspended mailboxes and
 * the truth stays exactly one filter away.
 */
export interface AccountStatusOverlay {
  label: string;
  reason: string;
  bucket: StatusBucket;
}

export function orgPausedOverlay(
  isOrgPaused: boolean,
): AccountStatusOverlay | null {
  return isOrgPaused
    ? { label: "Paused", reason: "Subscription paused", bucket: "paused" }
    : null;
}

/**
 * The short human reason phrase to append to the status badge label, e.g.
 * "Suspended · high spam rate". Prefers the API's structured status
 * (`why`, falling back to `what`) over the legacy engine-code mapping; only
 * when `structuredStatus` is null does it fall back to `humanizeWarmupReason`
 * over the raw pause/throttle reason code. Branches on the structured object /
 * warmupStatus, never on message strings. Returns null when there is no reason
 * to show (e.g. a healthy active account).
 */
export function accountStatusReason(a: EmailAccountListItem): string | null {
  const structured = a.structuredStatus;
  if (structured) {
    const phrase = structured.why?.trim() || structured.what?.trim();
    return phrase && phrase.length > 0 ? phrase : null;
  }
  const code = warmupStatusReason(a);
  return code ? humanizeWarmupReason(code) : null;
}

// Hours/minutes thresholds for the auto-resume ETA copy.
const MS_PER_MIN = 60_000;
const MS_PER_HOUR = 60 * MS_PER_MIN;
const MS_PER_DAY = 24 * MS_PER_HOUR;

/**
 * Humanize a future ISO datetime (a cool-down / auto-resume ETA) as a relative
 * countdown — "in ~3h", "in ~2d", "in ~45m", or "shortly" once it's due. The
 * `lib/date.ts` relative-time helpers are all PAST-oriented ("3h ago"), so this
 * is the canonical home for the future-direction variant the cool-down log
 * needs. Returns null for null/unparseable input.
 */
export function formatRecoveryEta(
  iso: string | null | undefined,
  now: number = Date.now(),
): string | null {
  if (!iso) return null;
  const target = new Date(iso).getTime();
  if (Number.isNaN(target)) return null;
  const diff = target - now;
  if (diff <= 0) return "shortly";
  if (diff < MS_PER_HOUR)
    return `in ~${Math.max(1, Math.round(diff / MS_PER_MIN))}m`;
  if (diff < MS_PER_DAY) return `in ~${Math.round(diff / MS_PER_HOUR)}h`;
  return `in ~${Math.round(diff / MS_PER_DAY)}d`;
}

/**
 * Verdict for one stored DNS auth record (`spfData`/`dkimData`/`dmarcData`/
 * `mxRecordData`, read off `domainSecurity` — see `requiredDnsRecords`).
 *
 * THREE states, because the record's nullability carries a fact the four
 * `spfValid`-style booleans it replaced could not:
 *
 *  - `valid`     → the domain publishes the record.
 *  - `invalid`   → authoritative not-found. The domain does not publish it.
 *  - `unchecked` → never successfully resolved (resolver failure, or the domain
 *                  has never been swept). We know NOTHING.
 *
 * `unchecked` must never collapse into `invalid`. Doing so is what forced every
 * caller to cross-check `dnsCheckedAt`, and — on the warmup gate below — is what
 * took healthy accounts offline on a two-second SERVFAIL. There is deliberately
 * no `?? { isValid: false }` anywhere in this file.
 *
 * Structurally typed on `{ isValid }` so all four record shapes pass. Accepts
 * `undefined` too, for the same reason `dnsRows` guards its `fields`: a cache
 * entry written by an older client build carries no record key at all, and that
 * must degrade to `unchecked` rather than throw.
 */
export type DnsRecordVerdict = "valid" | "invalid" | "unchecked";

export function dnsRecordVerdict(
  record: { isValid: boolean } | null | undefined,
): DnsRecordVerdict {
  if (!record) return "unchecked";
  return record.isValid ? "valid" : "invalid";
}

// Required DNS records that are MISSING, in display order — DISPLAY ONLY. An
// UNCHECKED record is not missing and never appears here; only an authoritative
// not-found does. To predict a SERVER verdict use `dnsUnverifiedRecords` below,
// which is fail-closed like the backend is.
//
// Which records count mirrors the backend gate (`@trulyinbox/dns`
// dnsBlockingRecords, used by email-accounts.service `enableWarmup` and the
// warmup-worker DNS re-check): SPF + MX are always required; DKIM only for
// BUSINESS (custom-domain) accounts — a personal mailbox on a free consumer
// domain skips it; DMARC is optional/informational and never blocks.
export function dnsBlockingRecords(a: EmailAccountListItem): string[] {
  return missingDnsRecords(a).map((r) => r.label);
}

/** Same missing records as `dnsBlockingRecords`, with each record's key — for
 * deep-linking straight to that record's popup on the detail page. */
export function missingDnsRecords(
  a: EmailAccountListItem,
): Array<{ key: DomainSecurityFieldKey; label: string }> {
  return requiredDnsRecords(a)
    .filter((r) => r.verdict === "invalid")
    .map(({ key, label }) => ({ key, label }));
}

// Required DNS records that are NOT PROVEN VALID, in display order — an
// UNCHECKED record counts alongside an authoritative not-found.
//
// This is the FAIL-CLOSED twin of `dnsBlockingRecords`, and the two are not
// interchangeable. `dnsBlockingRecords` answers a display question ("what is
// the user missing?"), where painting a never-resolved record red would be a
// lie. This one answers the SERVER's question, and the server is fail-closed at
// every level: `dnsGatePassed` (@trulyinbox/dns) reads a missing record as
// blocking, and its SQL transcription (`dnsBlockedSql` in
// email-accounts.repository.ts) wraps every record in `COALESCE(…, 'false')`.
//
// Any client-side prediction of a server verdict must use THIS one, or the two
// contradict each other on an unswept account: filtering by "Not ready" returned
// rows the table painted "Warming", and the warmup toggle rendered enabled on an
// account whose enable call 422s.
/** The two fields the DNS gate reads. See `WarmupControlAccount` above. */
export type DnsRecordFields = Pick<
  EmailAccountListItem,
  "emailAddress" | "domainSecurity"
>;

export function dnsUnverifiedRecords(a: DnsRecordFields): string[] {
  return requiredDnsRecords(a)
    .filter((r) => r.verdict !== "valid")
    .map((r) => r.label);
}

// Which records gate, in display order — the single rule both predicates above
// read, so they can never drift on WHICH records count while differing on how
// `unchecked` is treated.
function requiredDnsRecords(
  a: DnsRecordFields,
): Array<{ key: DomainSecurityFieldKey; label: string; verdict: DnsRecordVerdict }> {
  const personal = isPersonalEmail(a.emailAddress);
  // Records come off `domainSecurity`, which is now the only copy on the wire —
  // the list item's top-level `spfData`/`dkimData`/`mxRecordData` were removed
  // once this nested snapshot carried the same objects plus the bands derived
  // from them. Optional-chained rather than defaulted: an account with no
  // snapshot at all (never swept) yields `undefined` per record, which
  // `dnsRecordVerdict` reads as `unchecked` — the same verdict a null column
  // produced before, and deliberately NOT `invalid`.
  const ds = a.domainSecurity;
  return [
    { key: "spf", label: "SPF", verdict: dnsRecordVerdict(ds?.spfData) },
    ...(personal
      ? []
      : [{ key: "dkim" as const, label: "DKIM", verdict: dnsRecordVerdict(ds?.dkimData) }]),
    { key: "mx", label: "MX", verdict: dnsRecordVerdict(ds?.mxRecordData) },
  ];
}

/**
 * Whether a disconnected mailbox is DWD-managed (bulk-imported via
 * Domain-Wide-Delegation tenant access — Google Workspace or Microsoft 365)
 * and therefore reconnects through the shared "Reconnect required" popup
 * (`useDwdReconnectPrompt`) instead of the generic disabled Start-warmup
 * control every other disconnected account renders. TRU-1181: for such an
 * account the Start-warmup control must stay ENABLED — clicking it opens the
 * popup, whose Reconnect button reconnects via the org's existing tenant
 * access with no OAuth prompt (see `useReconnectAccount`).
 */
export function needsDwdReconnectPrompt(a: WarmupControlAccount): boolean {
  return a.status !== "CONNECTED" && a.dwdWorkspaceId != null;
}

/**
 * The `ProviderIcon` key for an account: a Google mailbox imported through a
 * Workspace directory (DWD) gets the directory mark, everything else its own provider.
 */
export function accountProviderIconKey(a: {
  provider: string;
  dwdWorkspaceId?: number | null;
}): string {
  const provider = a.provider.toUpperCase();
  const isGoogle = provider === "GMAIL" || provider === "GOOGLE_WORKSPACE";
  return isGoogle && a.dwdWorkspaceId != null ? "GOOGLE_WORKSPACE_DIRECTORY" : a.provider;
}

/** Shown on the disabled Reconnect of a partner-managed mailbox. */
export const PARTNER_RECONNECT_HINT = "Contact your Partner Admin";

/**
 * How a mailbox gets reconnected from the accounts table, or `null` when it
 * needs no reconnect. Two cases need one: a DISCONNECTED mailbox, and a
 * `SUSPENDED_AUTH` one — still `status: CONNECTED`, but its credentials are
 * dead, and only a successful reconnect lifts that suspension (the worker
 * restores it on the CONNECTED event; auto-resume never touches it). Every
 * `SUSPENDED_AUTH` reason qualifies, `SEND_FAILURES_EXCEEDED` included — that
 * one can also be plain-Resumed.
 *
 *  - `partner`: nothing in this UI can reconnect it — only the partner's own
 *    connect-API call can — so the control renders disabled.
 *  - `dwd`: reconnects through its workspace's Manage Mailboxes screen.
 *  - `direct`: the shared `useReconnectAccount` flow (OAuth consent / SMTP form).
 *
 * Partner is checked first: it is the one case where clicking can't help.
 */
export function reconnectRoute(
  a: WarmupControlAccount,
): "partner" | "dwd" | "direct" | null {
  const needsReconnect =
    a.status !== "CONNECTED" || a.accountState === AccountState.SUSPENDED_AUTH;
  if (!needsReconnect) return null;
  if (a.partnerIntegrationId != null) return "partner";
  if (a.dwdWorkspaceId != null) return "dwd";
  return "direct";
}

// Whether warmup can be switched on. Mirrors the backend enable hard-gate: the
// mailbox must be CONNECTED and every required record must be PROVEN valid.
// Returns a user-facing message explaining why warmup can't be switched on, or
// null when it can. Used to disable the toggle with a tooltip instead of letting
// an OFF→ON flip fail at the server.
//
// Reads `dnsUnverifiedRecords`, not `dnsBlockingRecords`: the API's gate is
// fail-closed, so an unchecked record 422s on click. Showing an enabled toggle
// there is worse than showing a disabled one with a reason.
export function warmupEnableBlockedReason(
  a: WarmupControlAccount,
): string | null {
  if (a.status !== "CONNECTED") {
    // A disconnected DWD-managed mailbox is NOT blocked here — the caller
    // gates the click through `useDwdReconnectPrompt` instead, so the control
    // must stay enabled rather than disabled+tooltipped. See
    // `needsDwdReconnectPrompt`.
    if (needsDwdReconnectPrompt(a)) return null;
    return "Reconnect this mailbox to enable warmup";
  }
  const unverified = dnsUnverifiedRecords(a);
  if (unverified.length > 0) {
    return `Fix DNS records (${unverified.join(", ")}) to enable warmup`;
  }
  // The mailbox send/receive check — the API's SECOND hard gate, asserted after
  // the DNS one (`assertMailboxVerified`, 022408/022409), so it is checked
  // after DNS here too: a mailbox that is both DNS-broken and mid-check gets
  // the DNS message, which is the half the user can act on today.
  //
  // Two states, two different stories — one is progress to wait out, the other
  // is stuck until someone acts. Same split `accountStatusLabel` makes for the
  // badge, and the same wording as the enable-error toasts in
  // `use-account-detail.ts`, so the toggle and the toast never disagree.
  if (a.accountState === "MAILBOX_VERIFYING") {
    return "Checking this mailbox can send and receive — warmup starts automatically when it passes";
  }
  if (a.accountState === "MAILBOX_VERIFY_FAILED") {
    return "Mailbox check failed — use Re-check on the Setup & authentication card";
  }
  return null;
}
